import Head from "next/head";
import { Content, GoogleJobsSchema } from "@/ui";
import { getRoute } from "@/functions/getRoute";
import { getGlobal } from "@/functions/getGlobal";
import { jobs_helper } from "@/helpers/jobs_helper";
import * as additionalComponents from "./__components";
import { createTitle } from "@/functions/createTitle";
import { assets_helper } from "@/helpers/asset_helper";

export default function JobPage({ job, content }) {
  const global = getGlobal();
  const job_types = jobs_helper.getCategoryValues("93edf168-6d0b-41ef-ac10-eb7252afcc68", job);
  const site_google_logo_link = assets_helper.find("Google Jobs Logo", "name");

  const [employment_type] = job_types.map(
    (i) =>
      ({
        "f920d277-1ee8-4091-aa03-5f61391650ca": "FULL_TIME", // Permanent
        "d9aedd4f-ec71-406e-87df-0f805d8e276a": "FULL_TIME",
        "918332c0-3302-4f3e-a68e-3633a4d50897": "PART_TIME",
        "7af6904b-c666-4823-b0cc-85e40c834357": "CONTRACTOR",
        "42df5171-37e8-4b56-bea9-98af295b7708": "TEMPORARY",
      })[i.id],
  );

  return (
    <>
      <Head>
        <meta property="og:title" content={job.title} />
        <meta property="og:url" content={`/jobs/${job.url_slug}`} />
        <meta name="twitter:title" content={job.title} />
        <meta
          name="image"
          property="og:image"
          content={job.image ?? "https://cdn.sourceflow.co.uk/__gallery/9ec4a470-2ceb-4b64-b0a5-774ed3bebf24"}
        />
        <meta
          name="twitter:image"
          content={job.image ?? "https://cdn.sourceflow.co.uk/__gallery/9ec4a470-2ceb-4b64-b0a5-774ed3bebf24"}
        />
      </Head>
      <GoogleJobsSchema
        job={job}
        employment_type={employment_type}
        site_name={global["_theme.company.name"]}
        site_domain_link={global["_theme.company.domain"]}
        site_google_logo_link={site_google_logo_link?.file}
      />
      <Content items={content} additionalComponents={additionalComponents} />
    </>
  );
}

export async function getStaticProps({ params: { url_slug } }) {
  const job = jobs_helper.find(url_slug);
  const specialityIds = jobs_helper.getCategoryValueIds("6c946dcb-e5cb-418b-90b2-61d80e5b49eb", job);
  const related = jobs_helper.fetch({
    limit: 10,
    exclude: [job.id],
    filter: (i) => specialityIds.some((s) => JSON.stringify(i.categories).includes(s)),
  });

  return {
    props: {
      meta: {
        title: createTitle(job.title),
      },
      job,
      content: [
        {
          component: "BreadcrumbNavigation",
          props: {
            items: [
              {
                label: "Find a Job",
                href: getRoute("jobs"),
              },
              {
                label: job.title,
                href: getRoute("job", { url_slug }),
              },
            ],
          },
        },
        {
          component: "Header",
          props: {
            className: "bg-primary text-white",
            title: job.title,
            back: {
              path: "page.job.component.Header.back",
              placeholder: "Back to job search",
              href: getRoute("jobs"),
            },
          },
        },
        {
          component: "JobPageContent",
          props: {
            ...job,
            related: related.map((i) => ({
              ...i,
              href: getRoute("jobs", { url_slug: i.url_slug }),
            })),
          },
        },
      ],
    },
  };
}

export async function getStaticPaths() {
  return { paths: jobs_helper.toPaths(), fallback: false };
}
